import { useState, useEffect } from 'react';
import {
  Button,
  CloudIcon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  LogoutIcon,
  Text,
  WarningIcon,
} from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../features/auth/stores/authStore';
import { Routes as AppRoutes } from '../../../routes';

interface ConnectionErrorDetails {
  message: string;
  retryQueue: () => Promise<void>;
  isColdStart?: boolean; // True for ECONNREFUSED/503/504, false for other errors
}

/**
 * Dialog component for displaying connection errors to users.
 *
 * Listens to the global 'auth:network-error' event dispatched by the axios client
 * when token refresh fails due to network/infrastructure issues (e.g., cold starts).
 *
 * Features:
 * - Non-blocking modal dialog
 * - User can retry the failed request queue
 * - User can dismiss and continue (stays logged in)
 * - Loading state during retry
 * - Error feedback if retry fails
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <ConnectionErrorDialog />
 * ```
 */
export function ConnectionErrorDialog() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [isColdStart, setIsColdStart] = useState(false);
  const [retryQueue, setRetryQueue] = useState<(() => Promise<void>) | null>(
    null
  );

  useEffect(() => {
    const handleNetworkError = (event: Event) => {
      const customEvent = event as CustomEvent<ConnectionErrorDetails>;
      const { message, retryQueue, isColdStart = false } = customEvent.detail;

      setMessage(message);
      setRetryQueue(() => retryQueue); // Wrap in arrow function to avoid immediate invocation
      setIsColdStart(isColdStart);
      setOpen(true);
      setRetryError(null);
    };

    window.addEventListener('auth:network-error', handleNetworkError);

    return () => {
      window.removeEventListener('auth:network-error', handleNetworkError);
    };
  }, []);

  const handleRetry = async () => {
    if (!retryQueue) {
      return;
    }

    setRetrying(true);
    setRetryError(null);

    try {
      await retryQueue();
      // Success! Close dialog
      setOpen(false);
      setRetrying(false);
    } catch (error) {
      // Retry failed
      setRetrying(false);
      setRetryError(
        error instanceof Error
          ? error.message
          : 'Retry failed. Please try again.'
      );
    }
  };

  const handleDismiss = () => {
    setOpen(false);
    setRetrying(false);
    setRetryError(null);
  };

  const handleLogout = () => {
    console.log('👤 User initiated logout from ConnectionErrorDialog');
    logout('connection_issue_logout');
    setOpen(false);
    navigate(AppRoutes.login);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) {
          handleDismiss();
        }
      }}
    >
      <DialogContent
        className="max-w-sm"
        aria-labelledby="connection-error-dialog-title"
        aria-describedby="connection-error-dialog-description"
      >
        <DialogHeader
          id="connection-error-dialog-title"
          className="flex items-center gap-3"
        >
          {isColdStart ? (
            <>
              <CloudIcon
                aria-hidden="true"
                className="size-5 text-content-success"
              />
              Starting Up...
            </>
          ) : (
            <>
              <WarningIcon
                aria-hidden="true"
                className="size-5 text-content-warning"
              />
              Connection Issue
            </>
          )}
        </DialogHeader>
        <DialogBody>
          <Text
            as="p"
            variant="body1"
            color="secondary"
            id="connection-error-dialog-description"
            className="whitespace-pre-line"
          >
            {message}
          </Text>
          {retryError && (
            <Text
              as="p"
              variant="body2"
              className="mt-4 rounded-lg bg-surface-error-subtle p-3 text-content-error"
            >
              {retryError}
            </Text>
          )}
          <Text as="p" variant="body2" color="secondary" className="mt-4">
            You can retry the connection, dismiss this message and continue
            browsing, or sign out.
          </Text>
        </DialogBody>
        <DialogFooter className="sm:justify-between">
          <Button variant="outlineDanger" onClick={handleLogout}>
            <LogoutIcon aria-hidden="true" className="size-4" />
            Sign Out
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDismiss}>
              Dismiss
            </Button>
            <Button
              variant="neutral"
              onClick={handleRetry}
              disabled={retrying}
              loading={retrying}
              autoFocus
            >
              {retrying ? 'Retrying...' : 'Retry'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
